/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from 'react'
import { useAppDispatch } from 'apps/placeme/src/redux/hooks'
import { useSelector } from 'react-redux'
import { useFetchTileData } from 'apps/placeme/src/customHooks/useFetchTileData'
import { RootState } from 'apps/placeme/src/redux/store'
import { AuthContext } from '@dataplace.ai/features'
import { deleteTileAction, saveChosenRangeAction } from 'apps/placeme/src/features/Analyse/slice/analysisSlice'
import { Loader } from '@dataplace.ai/ui-components/atoms'
import { getAxios } from '@dataplace.ai/functions/utils'
import { ENDPOINTS } from 'apps/placeme/src/constants/endpoints'
import { useSignal } from '@preact/signals-react'
import { IChainBenchmarkData, IChainBenchmarkTileBody } from './@types/IChainBenchmark'
import ChainBenchmarkResult from './ChainBenchmarkResult'
import ChainBenchmarkForm from './ChainBenchmarkForm'
import { SelectedLabelProps } from './ChainBenchmarkModel'

interface ChainBenchmarkTileProps {
  data: IChainBenchmarkData
  tileId: string
  body: IChainBenchmarkTileBody | null
  categoryId: string
  endpoint: string
  selectedLabel: SelectedLabelProps | null
  setSelectedLabel: Dispatch<SetStateAction<SelectedLabelProps | null>>
}

function ChainBenchmarkTile({
  data, tileId, body, categoryId, endpoint, selectedLabel, setSelectedLabel,
}: ChainBenchmarkTileProps) {
  const authContext = useContext(AuthContext)
  const dispatch = useAppDispatch()
  const { analyseId } = useSelector((state: RootState) => state.location)
  const areLabelsFetched = useSignal(false)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [accepted, setAccepted] = useState(false)
  const [token, setToken] = useState('')
  const isFetchingLabels = useSignal(false)
  const [labelDropdownOptions, setLabelDropdownOptions] = useState<SelectedLabelProps[]>([])

  const { fetchData } = useFetchTileData(
    data,
    endpoint,
    tileId,
    categoryId,
    body,
    setIsSubmitting,
    true,
  )
  const handleSubmit = () => {
    if (isSubmitting) return

    setAccepted(true)
    setIsSubmitting(true)
    fetchData()
  }

  const handleDeleteTile = useCallback(() => {
    if (!token) return
    dispatch(deleteTileAction(token, categoryId, tileId, analyseId || ''))
  }, [dispatch, token, categoryId, tileId, analyseId])

  useEffect(() => {
    authContext?.userData?.user?.getIdToken()?.then((response) => {
      setToken(response)
    })
  }, [authContext])

  useEffect(() => {
    if (!data?.value?.benchmark?.catchmentId) return

    dispatch(saveChosenRangeAction(categoryId, tileId, {
      id: '',
      value: 0,
      type: 'line',
      catchmentId: data?.value?.benchmark.catchmentId || '',
    }))
  }, [data?.value?.benchmark?.catchmentId])

  const fetchLabels = useCallback(async () => {
    isFetchingLabels.value = true
    const axiosInstance = await getAxios()

    const response = await axiosInstance.get(ENDPOINTS.LABELS)
    areLabelsFetched.value = true
    isFetchingLabels.value = false

    if (!response) return

    setLabelDropdownOptions(response.data.labels)
  }, [])

  useEffect(() => {
    if (labelDropdownOptions.length || isFetchingLabels.value || areLabelsFetched.value) return
    fetchLabels()
  }, [labelDropdownOptions, fetchLabels, areLabelsFetched.value, isFetchingLabels.value])

  if ((isSubmitting || isFetchingLabels.value) && (data?.loading || !data?.value)) return <Loader />

  return (
    (!accepted && !isSubmitting) && !data?.value
      ? (
        <ChainBenchmarkForm
          handleDeleteTile={handleDeleteTile}
          handleSubmit={handleSubmit}
          labelDropdownOptions={labelDropdownOptions}
          selectedLabel={selectedLabel}
          setSelectedLabel={setSelectedLabel}
        />
      )
      : <ChainBenchmarkResult data={data} />
  )
}

export default ChainBenchmarkTile
